import { Alert, Form, Input, Row, Select } from "antd"
import { useSelector } from "react-redux";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { CashboxCategory } from "src/common/models/cashboxCategory";
import { getInputRequiredRule } from "src/common/util";
import { Col8 } from "src/components/Columns";
import { FormActionButtons } from "src/components/FormActionButtons";
import { RootState } from "src/state/reducer";
import { UPSERT_CASHBOX_CATEGORY_API } from "../state/actionTypes";
import { SystemDescriptions } from "src/common/descriptions";
import { CashboxError } from "../state/types";

interface Props {
    initialValues?: CashboxCategory
    onFinish: (cashboxCategory: CashboxCategory) => void
    onCancel: VoidFunction
}

interface ReduxProps {
    isUpsertingCategory: boolean
    error?: CashboxError
}

export const CashboxCategoryForm = (props: Props) => {

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        isUpsertingCategory: serviceLoading(state, [UPSERT_CASHBOX_CATEGORY_API]),
        error: state.cashbox.error,
    }))

    const descriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_CASHBOX_CATEGORY
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const [form] = Form.useForm();

    const onFinish = (formValues) => {
        const cashboxCategory: CashboxCategory = {
            name: formValues.name,
            description: formValues.description,
            isEditable: true,
            type: formValues.type,
            createdAt: '',
            userId: 0,
        }

        props.onFinish(cashboxCategory)
    }

    const getButtonStatus = (): boolean => {
        return props.initialValues
            ? false
            : !form.isFieldsTouched(true) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={props.onCancel}
            actionLoading={reduxProps.isUpsertingCategory}
        />
    )

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.NAME.LABEL}
                        name={"name"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.NAME.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.NAME.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.DESCRIPTION.LABEL}
                        name={"description"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule(descriptions.FORM.DESCRIPTION.LABEL),
                        ]}
                    >
                        <Input
                            placeholder={descriptions.FORM.DESCRIPTION.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.TYPE.LABEL}
                        name={"type"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule(descriptions.FORM.TYPE.LABEL),
                        ]}
                    >
                        <Select
                            options={[
                                {
                                    value: "I",
                                    label: descriptions.INCOME,
                                    disabled: true,
                                },
                                {
                                    value: "O",
                                    label: descriptions.OUTCOME,
                                },
                            ]}
                            placeholder={descriptions.FORM.TYPE.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col8>
            </Row>
            {
                reduxProps.error &&
                <Alert
                    message={"ERROR"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
